import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchPolicies, fetchPoliciesForMultipleUsers } from "../policies/policySlice";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { firebaseCache } from "../../utils/firebaseCache";
import { incrementActionCounter } from "../../utils/actionCounters";
// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@mui/icons-material/Phone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useResponsiveFonts, truncateName, truncateText, truncatePhoneNumber } from '../../utils/responsiveFonts';
import { navigateWithScrollReset } from '../../utils/ScrollRestoration';
import { getPolicyCountsByCategory } from '../../utils/countUtils';
import RemindDialog from '../../components/common/RemindDialog';
import { handleMobileRefresh } from '../../utils/mobileRefresh';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useAdminStaffData } from '../../hooks/useAdminStaffData';
import MobileAdminFinance from "./MobileAdminFinance";
dayjs.extend(customParseFormat);

// Helper to convert values like "25,000", "₹25000", null -> number
const toNumber = (val) => {
  if (val == null) return 0;
  const cleaned = String(val).replace(/[^0-9.-]/g, "");
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
};

const MobileBalance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { policies, loading, initialized } = useSelector((state) => state.policies);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const fonts = useResponsiveFonts();

  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("motor");
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [displayedPolicies, setDisplayedPolicies] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const contentRef = useRef(null);
  const isInitialMount = useRef(true); // Track initial mount to avoid saving empty state
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [selectedPolicyForRemind, setSelectedPolicyForRemind] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter state
  const [sortDirection, setSortDirection] = useState(null); // null, 'asc' (low to high), 'desc' (high to low)
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null);
  const [isFilterClosing, setIsFilterClosing] = useState(false);

  const categories = ["motor", "non-motor", "life"];
  const itemsPerPage = 10;

  // Calculate active filter count for badge
  const activeFilterCount = (sortDirection ? 1 : 0) + (dateRange.start && dateRange.end ? 1 : 0);

  // Refresh handler using shared refresh function
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes

    try {
      await handleMobileRefresh({
        userId: user?.uid,
        dispatch,
        setRefreshing: setIsRefreshing
      });
      console.log('✅ Balance data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing Balance:', error);
    }
  };

  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0 && !initialized) {
      console.log('Admin user (Mobile) - fetching aggregated policies for balance:', userIds);
      dispatch(fetchPoliciesForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      // For regular staff, only fetch if not already loaded or if needed
      if (policies.length === 0 && !initialized) {
        console.log('Staff user (Mobile) - fetching own policies for balance');
        dispatch(fetchPolicies(user.uid));
      }
    }
  }, [user?.uid, dispatch, isAdmin, userIds, staffLoading, initialized]);

  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('mobileBalanceList_filterState');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSortDirection(filters.sortDirection || null);
        // Restore date range
        if (filters.dateRange) {
          setDateRange({
            start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
            end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
          });
        }
      } catch (error) {
        console.error('Error restoring filter state:', error);
      }
    }
    // Delay setting isInitialMount to false to ensure all state updates complete
    setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
  }, []); // Run only on mount

  // Scroll to top when category or filters change
  useEffect(() => {
    if (!isInitialMount.current && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [selectedCategory, searchTerm, sortDirection, dateRange]);

  // Save filter state to sessionStorage whenever filters change (but not on initial mount)
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting with empty state
    if (isInitialMount.current) {
      return;
    }

    const filterState = {
      sortDirection,
      dateRange: {
        start: dateRange.start ? dateRange.start.toISOString() : null,
        end: dateRange.end ? dateRange.end.toISOString() : null
      }
    };
    sessionStorage.setItem('mobileBalanceList_filterState', JSON.stringify(filterState));
  }, [sortDirection, dateRange]);

  // Restore category from navigation state or sessionStorage
  useEffect(() => {
    // First try to restore from navigation state
    if (location.state?.fromCategory) {
      console.log('MobileBalance: Restoring category from state:', location.state.fromCategory);
      setSelectedCategory(location.state.fromCategory);
    } else {
      // If no navigation state, check sessionStorage (for swipe back or refresh)
      const storedCategory = sessionStorage.getItem('mobileBalanceList_selectedCategory');
      if (storedCategory) {
        console.log('MobileBalance: Restoring category from sessionStorage:', storedCategory);
        setSelectedCategory(storedCategory);
      }
    }
  }, [location.state]);

  // Save selected category to sessionStorage whenever it changes
  useEffect(() => {
    if (!isInitialMount.current) {
      sessionStorage.setItem('mobileBalanceList_selectedCategory', selectedCategory);
      console.log('MobileBalance: Saved category to sessionStorage:', selectedCategory);
    }
  }, [selectedCategory]);

  // Scroll restoration is now handled globally by MobileLayout

  const filteredPolicies = policies.filter((policy) => {
    // Exclude new leads
    if (policy.status === "New Lead") return false;

    // Category filter
    const categoryMatch = selectedCategory === "motor"
      ? policy.policyCategory === "motor"
      : selectedCategory === "non-motor"
        ? policy.policyCategory === "non-motor"
        : policy.policyCategoryType === selectedCategory;

    if (!categoryMatch) return false;

    // Calculate balance
    const toNumber = (val) => {
      if (val == null) return 0;
      const cleaned = String(val).replace(/[^0-9.-]/g, "");
      const num = Number(cleaned);
      return isNaN(num) ? 0 : num;
    };

    const premium = toNumber(policy.premiumAmount ?? policy.premium ?? policy.totalPremium);
    const amountPayable = toNumber(policy.amountPayable) || (premium - toNumber(policy.discount));
    const paid = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
    const balance = amountPayable - paid;

    // Skip if balance is 0 or negative
    if (balance <= 0) return false;

    // Date range filter (on createdAt timestamp)
    if (dateRange.start || dateRange.end) {
      const created = policy.createdAt ? (policy.createdAt.seconds ? dayjs(policy.createdAt.seconds * 1000) : dayjs(policy.createdAt)) : null;
      if (created && created.isValid()) {
        if (dateRange.start && created.isBefore(dateRange.start, 'day')) {
          return false;
        }
        if (dateRange.end && created.isAfter(dateRange.end, 'day')) {
          return false;
        }
      } else if (dateRange.start || dateRange.end) {
        // If date range is set but policy has no valid date, exclude it
        return false;
      }
    }

    // Search filter
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const searchClean = searchLower.replace(/-/g, "");
    const vehicleNumberClean = (policy.vehicleNumber || "")
      .toLowerCase()
      .replace(/-/g, "");

    return (
      (policy.name || "").toLowerCase().includes(searchLower) ||
      (policy.mobile || "").includes(searchTerm) ||
      vehicleNumberClean.includes(searchClean)
    );
  });

  // Apply sorting if sort direction is set
  const sortedPolicies = sortDirection
    ? [...filteredPolicies].sort((a, b) => {
      const premiumA = toNumber(a.premiumAmount ?? a.premium ?? a.totalPremium);
      const amountPayableA = toNumber(a.amountPayable) || (premiumA - toNumber(a.discount));
      const paidA = toNumber(a.paidAmount ?? a.amountPaid ?? a.amountReceived);
      const balanceA = amountPayableA - paidA;

      const premiumB = toNumber(b.premiumAmount ?? b.premium ?? b.totalPremium);
      const amountPayableB = toNumber(b.amountPayable) || (premiumB - toNumber(b.discount));
      const paidB = toNumber(b.paidAmount ?? b.amountPaid ?? b.amountReceived);
      const balanceB = amountPayableB - paidB;

      return sortDirection === 'asc' ? balanceA - balanceB : balanceB - balanceA;
    })
    : filteredPolicies; // Default: no sorting, show latest first (natural order from Firebase)

  // Calculate counts for each category based on policies with a balance AND date filter applied
  const policiesWithBalance = policies.filter(p => {
    if (p.status === "New Lead") return false;
    const premium = toNumber(p.premiumAmount ?? p.premium ?? p.totalPremium);
    const amountPayable = toNumber(p.amountPayable) || (premium - toNumber(p.discount));
    const paid = toNumber(p.paidAmount ?? p.amountPaid ?? p.amountReceived);
    const balance = amountPayable - paid;
    if (balance <= 0) return false;

    // Apply date range filter to tab counts
    if (dateRange.start || dateRange.end) {
      const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
      if (created && created.isValid()) {
        if (dateRange.start && created.isBefore(dateRange.start, 'day')) {
          return false;
        }
        if (dateRange.end && created.isAfter(dateRange.end, 'day')) {
          return false;
        }
      } else if (dateRange.start || dateRange.end) {
        return false;
      }
    }

    return true;
  });

  const categoryCounts = getPolicyCountsByCategory(policiesWithBalance);

  // Initialize displayed policies when filters change
  useEffect(() => {
    // Do not set displayed policies until initial data load has completed
    if (!initialized) return;

    if (sortedPolicies.length > 0) {
      // Show ALL policies initially, not just first page
      setDisplayedPolicies(sortedPolicies);
      setHasMore(false); // No more to load since we're showing all
    } else {
      setDisplayedPolicies([]);
      setHasMore(false);
    }
  }, [selectedCategory, searchTerm, policies.length, initialized, sortDirection, dateRange]);

  // Infinite scroll handler
  const loadMorePolicies = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      setDisplayedPolicies(prev => {
        const currentLength = prev.length;
        // Recalculate filtered policies inside the callback to get fresh data
        const currentFilteredPolicies = policies.filter((policy) => {
          const matchesCategory = selectedCategory === 'All' || policy.vehicleType === selectedCategory;
          const matchesSearch = policy.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            policy.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            policy.policyNumber?.toLowerCase().includes(searchTerm.toLowerCase());
          return matchesCategory && matchesSearch;
        });
        const nextPolicies = currentFilteredPolicies.slice(currentLength, currentLength + itemsPerPage);

        if (nextPolicies.length > 0) {
          setHasMore(currentLength + nextPolicies.length < currentFilteredPolicies.length);
          return [...prev, ...nextPolicies];
        } else {
          setHasMore(false);
          return prev;
        }
      });
      setIsLoading(false);
    }, 300);
  }, [isLoading, hasMore, policies, selectedCategory, searchTerm, itemsPerPage]);

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom && hasMore && !isLoading) {
        loadMorePolicies();
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoading, loadMorePolicies]);

  // Show loading state until data is actually loaded
  if (loading || !initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading policies...</Typography>
      </Box>
    );
  }

  const handleFabMenuOpen = (event) => {
    // Handle add policy menu
    // Debug logging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Add policy clicked');
    }
  };

  const handleDropdownOpen = (event) => {
    setDropdownAnchor(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setDropdownAnchor(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setDropdownAnchor(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Try different date formats
    const formats = ['DD-MM-YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY'];

    for (const format of formats) {
      const parsed = dayjs(dateString, format, true);
      if (parsed.isValid()) {
        return parsed.format('DD MMM, YYYY');
      }
    }

    // If no format works, try parsing as ISO or default
    const fallback = dayjs(dateString);
    return fallback.isValid() ? fallback.format('DD MMM, YYYY') : 'Invalid Date';
  };

  // Handle phone call
  const handlePhoneCall = (policy, event) => {
    event.stopPropagation();
    if (policy?.mobile) {
      window.location.href = `tel:${policy.mobile}`;
    }
  };

  // Format number with thousands separator
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  // Generate WhatsApp message for balance reminders
  const generateBalanceMessage = async (policy) => {
    const premium = toNumber(policy.premiumAmount ?? policy.premium ?? policy.totalPremium);
    const amountPayable = toNumber(policy.amountPayable) || (premium - toNumber(policy.discount));
    const paid = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
    const balanceAmount = amountPayable - paid;

    if (balanceAmount <= 0) return "";

    const policyType = policy.policyType || "Policy";
    const companyName = policy.policyCompany || "your insurer";
    const isMotor = policy.policyCategory === "motor";
    const vehicleModel = policy.vehicleModel || "";
    const vehicleMake = policy.vehicleMake || "";
    const vehicleNumber = policy.vehicleNumber || "";
    const clientName = policy.name || "";
    const greeting = clientName ? `Hi ${clientName}` : "Hi";

    let message = "";

    // Get agent details from Firestore with fallbacks
    let agentName = 'your agent';
    let agentMobile = '';

    try {
      if (user?.uid) {
        const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));
        if (agentDoc.exists()) {
          const agentData = agentDoc.data();
          agentName = agentData.name || agentData.displayName || 'your agent';
          agentMobile = agentData.phone || agentData.phoneNumber || '';
        }
      } else {
        // Fallback to policy data if user is not available
        agentName = policy.agentName || 'your agent';
        agentMobile = policy.agentMobile || '';
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      // Fallback to policy data if there's an error
      agentName = policy.agentName || 'your agent';
      agentMobile = policy.agentMobile || '';
    }

    if (isMotor && vehicleModel && vehicleNumber) {
      message = `${greeting}\nYour pending balance of *₹${balanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}* for your *${policyType} policy*, ${vehicleMake} ${vehicleModel} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    } else if (isMotor && vehicleNumber) {
      message = `${greeting}\nYour pending balance of *₹${balanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}* for your *${policyType} policy*, ${vehicleMake} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    } else {
      message = `${greeting}\nYour pending balance of *₹${balanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}* for your *${policyType}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    }

    return message;
  };

  // Handle WhatsApp redirect with improved iOS support
  const handleWhatsAppRedirect = (message) => {
    if (!message) {
      console.error("No message provided for WhatsApp");
      return;
    }

    const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIos) {
      // For iOS, use direct window.location.href
      // This will open the app if installed, or do nothing if not
      window.location.href = whatsappAppUrl;

      // No fallback to web version as it was causing issues
      // User will stay in the app if it opens, or nothing happens if not installed
    } else {
      // For Android/other devices, use iframe approach
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = whatsappAppUrl;
      document.body.appendChild(iframe);

      // Remove iframe after a short delay
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 100);

      // No need for blur detection or fallback to web version
      // as it was causing the web version to open even when app was launched
    }
  };

  const handleRemindClick = (policy, event) => {
    event.stopPropagation();
    setSelectedPolicyForRemind(policy);
    setRemindDialogOpen(true);
  };

  const handleRemindConfirm = async () => {
    if (selectedPolicyForRemind) {
      try {
        const message = await generateBalanceMessage(selectedPolicyForRemind);
        handleWhatsAppRedirect(message);

        if (user?.uid) {
          await incrementActionCounter(user.uid, 'balanceremindercounter');
        }
      } catch (error) {
        console.error('Error generating balance message:', error);
        // Fallback to a default message if there's an error
        handleWhatsAppRedirect("Your balance reminder couldn't be generated. Please try again or contact support.");
      }
    }
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  const handleRemindClose = () => {
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  // Filter handlers
  const handleFilterButtonClick = (event) => {
    event.stopPropagation();
    if (filterAnchorEl) {
      // If already open, start closing animation
      setIsFilterClosing(true);
      setTimeout(() => {
        setFilterAnchorEl(null);
        setIsFilterClosing(false);
      }, 200);
    } else {
      setFilterAnchorEl(event.currentTarget);
    }
  };

  const handleFilterClose = () => {
    setIsFilterClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setFilterAnchorEl(null);
      setIsFilterClosing(false);
    }, 200);
  };

  const handleSortChange = (direction) => {
    if (sortDirection === direction) {
      // Toggle off if clicking the same option
      setSortDirection(null);
    } else {
      setSortDirection(direction);
    }
  };

  const handleDateRangeCalendarChange = (date) => {
    const selectedDay = dayjs(date);

    if (showDateRangeCalendar === 'start') {
      // Setting start date
      setDateRange({
        start: selectedDay,
        end: dateRange.end && selectedDay.isAfter(dateRange.end) ? null : dateRange.end
      });
      // Automatically switch to end date selection if end is not set
      if (!dateRange.end) {
        setShowDateRangeCalendar('end');
      }
    } else if (showDateRangeCalendar === 'end') {
      // Setting end date
      // Validation: end date cannot be before start date
      if (selectedDay.isBefore(dateRange.start, 'day')) {
        // Don't set end date, keep calendar open for user to select valid date
        return;
      }
      setDateRange((prev) => ({
        start: prev.start,
        end: selectedDay
      }));
      // Close calendar after end date is selected
      setShowDateRangeCalendar(null);
    }
  };

  const getCalendarConfig = () => {
    try {
      const testDate = new Date();
      const testLocale = testDate.toLocaleDateString('en');
      return {
        calendarType: 'gregory',
        locale: 'en',
        maxDetail: 'month',
        minDetail: 'month'
      };
    } catch (error) {
      console.warn('Calendar locale detection failed, using fallback:', error);
      return {
        calendarType: 'gregory',
        locale: undefined,
        maxDetail: 'month',
        minDetail: 'month'
      };
    }
  };

  const clearFilters = () => {
    setSortDirection(null);
    setDateRange({ start: null, end: null });
    setShowDateRangeCalendar(null);
  };

  if (isAdmin) {
    return <MobileAdminFinance />;
  }

  return (
    <Box
      className="mobile-content-container"
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        pb: 10, // Add padding bottom to account for fixed navigation
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box sx={{
        backgroundColor: '#0063F7',
        px: { xs: 2, sm: 3, md: 4 },
        py: 1,
        height: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <Typography variant="h6" sx={{
          color: 'white',
          fontWeight: 600,
          fontSize: fonts.header.xs
        }}>
          Finance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            sx={{ color: 'white' }}
            onClick={() => setShowSearch(!showSearch)}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Category Tabs Section */}
      <Box sx={{
        position: 'fixed',
        top: showSearch ? 140 : 70,
        left: 0,
        right: 0,
        zIndex: 1002,
        backgroundColor: '#f5f5f5',
        px: 2,
        pt: 2.5, // Increased gap from header
        pb: 2,
        transition: 'top 0.3s ease'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
          {categories.map((category) => (
            <Box
              key={category}
              onClick={() => setSelectedCategory(category)}
              sx={{
                flex: 1,
                py: 0.6,
                px: 1,
                borderRadius: '15px',
                backgroundColor: selectedCategory === category ? '#E3F2FD' : 'white',
                border: `1px solid ${selectedCategory === category ? '#0063F7' : '#e0e0e0'}`,
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 0,
                transition: 'all 0.2s ease',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Box sx={{
                fontSize: '13.5px !important',
                fontWeight: 500,
                color: selectedCategory === category ? '#0063F7' : '#666',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                {category === 'motor' ? `Motor (${categoryCounts.motor || 0})` :
                  category === 'life' ? `Life (${categoryCounts.life || 0})` :
                    `Health (${categoryCounts['non-motor'] || 0})`}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{
        position: 'fixed',
        top: 70,
        left: 0,
        right: 0,
        zIndex: 1003,
        backgroundColor: showSearch ? 'white' : 'transparent',
        pointerEvents: showSearch ? 'all' : 'none',
        borderBottom: showSearch ? '1px solid #e0e0e0' : 'none',
        transition: 'all 0.3s ease'
      }}>
        <Box sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 1.5,
          height: 70,
          boxSizing: 'border-box',
          display: showSearch ? 'block' : 'none'
        }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by name, phone, or vehicle number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#757575' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      {/* Static Header Row */}


      {/* Balance Cards */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 2,
          pb: 10,
          mt: showSearch ? '216px' : '150px',
          overflowX: 'hidden'
        }}
      >

        {displayedPolicies.length > 0 ? (
          <>
            {displayedPolicies.map((policy) => {
              const premium = toNumber(policy.premiumAmount ?? policy.premium ?? policy.totalPremium);
              const amountPayable = toNumber(policy.amountPayable) || (premium - toNumber(policy.discount));
              const paid = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
              const balance = Math.max(amountPayable - paid, 0);

              return (
                <Paper
                  key={policy.id}
                  onClick={() => {
                    // Store current category in sessionStorage for swipe back support
                    sessionStorage.setItem('mobileBalance_selectedCategory', selectedCategory);
                    const navigationState = { policy, fromScreen: 'balance', fromCategory: selectedCategory };
                    navigate(`/policy/${policy.id}`, { state: navigationState });
                  }}
                  sx={{
                    p: { xs: 1, sm: 1.2, md: 1.7 },
                    mb: 1.5,
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    flex: '1 1 100%',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  {/* 3 Column Layout without headers */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.2fr 1.2fr',
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    alignItems: 'center',
                    width: '100%',
                    overflow: 'hidden'
                  }}>
                    {/* Column 1: Customer Name with Date */}
                    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <Typography sx={{
                        fontSize: '16px !important',
                        fontWeight: 600,
                        color: '#212121',
                        mb: 0.2,
                        lineHeight: 1.1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&.MuiTypography-root': {
                          fontSize: '16px !important'
                        }
                      }}>
                        {policy.name || 'Customer Name'}
                      </Typography>
                      <Typography sx={{
                        fontSize: '12px !important',
                        color: '#666',
                        lineHeight: 1.1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&.MuiTypography-root': {
                          fontSize: '12px !important'
                        }
                      }}>
                        {formatDate(policy.expiryDate)}
                      </Typography>
                    </Box>

                    {/* Column 2: Amount Payable */}
                    <Box sx={{ textAlign: 'left', minWidth: 0, overflow: 'hidden' }}>
                      <Typography sx={{
                        fontSize: '16px !important',
                        fontWeight: 600,
                        color: '#166434',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&.MuiTypography-root': {
                          fontSize: '16px !important'
                        }
                      }}>
                        ₹ {amountPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>

                    {/* Column 3: Balance */}
                    <Box sx={{ textAlign: 'left', minWidth: 0, overflow: 'hidden' }}>
                      <Typography sx={{
                        fontSize: '16px !important',
                        color: balance > 0 ? '#d32f2f' : '#166434',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&.MuiTypography-root': {
                          fontSize: '16px !important'
                        }
                      }}>
                        ₹ {Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons Row */}
                  <Box sx={{
                    display: 'flex',
                    gap: 2.5,
                    mt: 1.5,
                    width: '100%',
                    justifyContent: 'flex-start'
                  }}>
                    {/* Call Button */}
                    <Button
                      onClick={(e) => handlePhoneCall(policy, e)}
                      disabled={!policy?.mobile}
                      sx={{
                        flex: 1,
                        height: '22px',
                        borderRadius: '4px',
                        backgroundColor: '#0063F7 !important',
                        color: '#fff !important',
                        fontSize: '10px !important',
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.3,
                        px: 0.5,
                        py: 0,
                        minWidth: 0
                      }}
                      className="mobile-button-override"
                    >
                      <PhoneIcon sx={{ fontSize: '12px' }} />
                      Call
                    </Button>

                    {/* Remind Button */}
                    <Button
                      onClick={(e) => handleRemindClick(policy, e)}
                      sx={{
                        flex: 1,
                        height: '22px',
                        borderRadius: '4px',
                        border: '1px solid #166434 !important',
                        backgroundColor: 'transparent !important',
                        color: '#166434 !important',
                        fontSize: '10px !important',
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.3,
                        px: 0.5,
                        py: 0,
                        minWidth: 0
                      }}
                      className="mobile-button-override"
                    >
                      <NotificationsIcon sx={{ fontSize: '12px' }} />
                      Remind
                    </Button>
                  </Box>
                </Paper>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                  Loading more policies...
                </Typography>
              </Box>
            )}

          </>
        ) : (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            px: 2
          }}>
            <Typography sx={{
              fontSize: '16px',
              color: '#757575',
              mb: 1
            }}>
              No balance records found
            </Typography>
            <Typography sx={{
              fontSize: '14px',
              color: '#9E9E9E'
            }}>
              Great time to follow up with new leads!
            </Typography>
          </Box>
        )}
      </Box>


      {/* Bottom Navigation */}
      <Box className="bottom-navigation" sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        px: 0.5,
        py: 1,
        height: 80
      }}>
        {/* Commission */}
        {isAdmin && (
          <Box
            onClick={() => navigateWithScrollReset(navigate, '/commission', contentRef)}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              py: 0.5
            }}
          >
            <CurrencyRupeeIcon sx={{ color: '#757575', mb: 0.5, fontSize: 20 }} />
            <Typography sx={{ fontSize: '9px', color: '#757575', letterSpacing: '-0.02em' }}>
              Commission
            </Typography>
          </Box>
        )}
        {/* Renewal */}
        <Box
          onClick={() => navigateWithScrollReset(navigate, '/renewals', contentRef)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5
          }}
        >
          <AutorenewIcon sx={{ color: '#757575', mb: 0.5, fontSize: 20 }} />
          <Typography sx={{ fontSize: '9px', color: '#757575', letterSpacing: '-0.02em' }}>
            Renewal
          </Typography>
        </Box>

        {/* Balance */}
        <Box
          onClick={() => navigateWithScrollReset(navigate, '/balance', contentRef)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5
          }}
        >
          <AccountBalanceWalletIcon sx={{ color: '#0063F7', mb: 0.5, fontSize: 20 }} />
          <Typography sx={{ fontSize: '9px', color: '#0063F7', fontWeight: 500, letterSpacing: '-0.02em' }}>
            Finance
          </Typography>
        </Box>


        {/* Client */}
        <Box
          onClick={() => navigateWithScrollReset(navigate, '/client', contentRef)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5
          }}
        >
          <PeopleIcon sx={{ color: '#757575', mb: 0.5, fontSize: 20 }} />
          <Typography sx={{ fontSize: '9px', color: '#757575', letterSpacing: '-0.02em' }}>
            Client
          </Typography>
        </Box>

        {/* Policy */}
        <Box
          onClick={() => navigateWithScrollReset(navigate, '/policy', contentRef)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5
          }}
        >
          <DescriptionIcon sx={{ color: '#757575', mb: 0.5, fontSize: 20 }} />
          <Typography sx={{ fontSize: '9px', color: '#757575', letterSpacing: '-0.02em' }}>
            Policy
          </Typography>
        </Box>

      </Box>

      {/* Filter Box */}
      {Boolean(filterAnchorEl) && (
        <Box
          data-filter-box="true"
          sx={{
            position: 'fixed',
            bottom: 170, // Position above FAB
            right: 20,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            zIndex: 1300,
            width: '95vw',
            maxWidth: 360,
            maxHeight: '70vh',
            overflowY: 'auto',
            p: 1.5,
            transformOrigin: 'bottom right',
            animation: isFilterClosing
              ? 'collapseToFab 0.2s ease-in forwards'
              : 'expandFromFab 0.3s ease-out',
            '@keyframes expandFromFab': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.3)',
                transformOrigin: 'bottom right'
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)',
                transformOrigin: 'bottom right'
              }
            },
            '@keyframes collapseToFab': {
              '0%': {
                opacity: 1,
                transform: 'scale(1)',
                transformOrigin: 'bottom right'
              },
              '100%': {
                opacity: 0,
                transform: 'scale(0.3)',
                transformOrigin: 'bottom right'
              }
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Stack spacing={1.5}>
            {/* Sort by Balance */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Sort by Balance</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                >
                  <FormControlLabel
                    value="asc"
                    control={
                      <Radio
                        size="small"
                        onClick={(e) => {
                          if (sortDirection === 'asc') {
                            e.preventDefault();
                            setSortDirection(null);
                          }
                        }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 14 }}>Low to High</Typography>}
                  />
                  <FormControlLabel
                    value="desc"
                    control={
                      <Radio
                        size="small"
                        onClick={(e) => {
                          if (sortDirection === 'desc') {
                            e.preventDefault();
                            setSortDirection(null);
                          }
                        }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 14 }}>High to Low</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider sx={{ my: 0.75 }} />

            {/* Date Range Filter */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Date Range</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                  sx={{
                    flex: 1,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                      borderColor: '#E0EBFF'
                    }
                  }}
                >
                  {dateRange.start ? dateRange.start.format('DD MMM') : 'START'}
                </Button>
                <Button
                  variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (dateRange.start) {
                      setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end');
                    }
                  }}
                  sx={{
                    flex: 1,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                      borderColor: '#E0EBFF'
                    }
                  }}
                  disabled={!Boolean(dateRange.start)}
                >
                  {dateRange.end ? dateRange.end.format('DD MMM') : 'END'}
                </Button>
              </Stack>
              {showDateRangeCalendar && (
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1300,
                    boxShadow: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    width: '100%',
                    overflow: 'hidden',
                    padding: '6px',
                    mt: 1,
                    '& .react-calendar': {
                      width: '100%',
                      maxWidth: '100%',
                      minHeight: '160px',
                      border: 'none',
                      fontFamily: 'Roboto',
                      '&__navigation': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 2px',
                        margin: '0 0 4px 0',
                        '&__label': {
                          minWidth: '110px',
                          textAlign: 'center',
                          flexGrow: 0,
                          fontWeight: '600 !important',
                          margin: 0,
                          padding: '2px 0',
                          fontSize: '12px',
                          lineHeight: '1.1'
                        },
                        '&__arrow': {
                          minWidth: '24px',
                          padding: '2px 0px',
                          margin: 0,
                          fontSize: '16px'
                        }
                      },
                      '&__month-view__weekdays': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0 2px',
                        marginBottom: '1px',
                        '&__weekday': {
                          width: '26px',
                          padding: '1px 0',
                          textAlign: 'center',
                          fontSize: '9px',
                          fontWeight: '600 !important',
                          textTransform: 'uppercase',
                          color: '#666 !important',
                          backgroundColor: 'transparent !important',
                          border: 'none !important',
                          '& abbr': {
                            textDecoration: 'none',
                            display: 'inline-block',
                            width: '100%',
                            textAlign: 'center'
                          }
                        }
                      },
                      '&__month-view__days': {
                        display: 'grid !important',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '1px',
                        padding: '0 2px',
                        '&__day': {
                          width: '26px',
                          height: '26px',
                          margin: '0 auto',
                          fontWeight: '600 !important',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          '&--neighboringMonth': {
                            opacity: 0.5
                          },
                          '&--weekend': {
                            color: '#212121 !important'
                          },
                          '&--now': {
                            fontWeight: 'bold',
                            color: '#004DC2 !important',
                            backgroundColor: 'transparent !important',
                            '&:enabled:hover': {
                              backgroundColor: '#E0EBFF !important'
                            }
                          }
                        }
                      }
                    },
                    '& .react-calendar__tile': {
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '13px',
                      color: '#212121 !important',
                      transition: 'all 0.2s ease',
                      '&:enabled:hover': {
                        backgroundColor: '#E0EBFF !important',
                        color: '#212121 !important'
                      },
                      '&--active, &--active:enabled, &--active:enabled:hover': {
                        backgroundColor: '#004DC2 !important',
                        color: '#fff !important',
                        borderRadius: '50%',
                        border: 'none',
                        boxShadow: 'none',
                        outline: 'none'
                      }
                    },
                    "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                    },
                    "& .react-calendar__month-view__days__day--weekend": {
                      color: '#212121 !important',
                    },
                    "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                      backgroundColor: '#E0EBFF !important',
                    },
                    "& .react-calendar__navigation button": {
                      color: '#333 !important',
                      background: 'none !important',
                      fontWeight: '600 !important',
                      border: 'none !important',
                      outline: 'none !important',
                      '&:hover': {
                        backgroundColor: '#E0EBFF !important',
                        color: '#333 !important',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent !important',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                      },
                      '&:active': {
                        backgroundColor: 'transparent !important',
                      },
                    },
                    "& .react-calendar__navigation__label": {
                      color: '#333 !important',
                      background: 'transparent !important',
                      fontWeight: '600 !important',
                      '&:hover': {
                        backgroundColor: 'transparent !important',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent !important',
                        outline: 'none !important',
                      },
                    },
                  }}
                >
                  {(() => {
                    try {
                      return (
                        <Calendar
                          value={dateRange.start?.toDate() || dateRange.end?.toDate()}
                          onChange={handleDateRangeCalendarChange}
                          {...getCalendarConfig()}
                        />
                      );
                    } catch (error) {
                      console.error('Date range calendar rendering failed:', error);
                      return (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                          Calendar could not be loaded.
                        </Box>
                      );
                    }
                  })()}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                {showDateRangeCalendar ? `Select date` : 'Click to select date range'}
              </Typography>
            </Box>

            <Divider sx={{ my: 0.75 }} />

            {/* Action Buttons */}
            <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
              <Button
                onClick={clearFilters}
                size="small"
                sx={{
                  flex: 1,
                  '&:hover': {
                    backgroundColor: '#F5F5F5'
                  }
                }}
              >
                Clear filters
              </Button>
              <Button
                variant="contained"
                onClick={handleFilterClose}
                size="small"
                sx={{ flex: 1 }}
              >
                Done
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Filter FAB Button */}
      <Box
        onClick={handleFilterButtonClick}
        sx={{
          position: 'fixed',
          bottom: 100, // Above the bottom navigation (80px height + 20px padding)
          right: { xs: 16, sm: 24 },
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          backgroundColor: '#0063F7',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0, 99, 247, 0.3)',
          zIndex: 1001,
          '&:active': {
            transform: 'scale(0.95)',
          },
          '&:hover': {
            backgroundColor: '#0051d4',
            boxShadow: '0 6px 20px rgba(0, 99, 247, 0.4)',
          },
        }}
      >
        <Badge
          color="primary"
          badgeContent={activeFilterCount}
          invisible={activeFilterCount === 0}
        >
          <FilterAltOutlinedIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
        </Badge>
      </Box>

      {/* Remind Dialog */}
      <RemindDialog
        isOpen={remindDialogOpen}
        onClose={handleRemindClose}
        onConfirm={handleRemindConfirm}
        message="Send balance reminder via WhatsApp?"
      />
    </Box>
  );
};

export default MobileBalance;
